import { useEffect, useState, useCallback, useRef } from 'react'
import { lumi } from '@/lib/lumi'
import {Save, Loader2, Upload, X, ImageIcon, BarChart3, ExternalLink} from 'lucide-react'
import toast from 'react-hot-toast'
import type { SiteSetting } from '@/types'
import { useAuth } from '@/hooks/useAuth'

interface SettingField {
  key: string
  label: string
  placeholder: string
  type?: 'text' | 'textarea' | 'url'
  category: string
  hint?: string
}

const FIELDS: SettingField[] = [
  { key: 'site_title', label: 'Nome do Site', placeholder: 'CARLOS FELIPE', type: 'text', category: 'seo', hint: 'Exibido no cabeçalho e aba do navegador.' },
  { key: 'meta_description', label: 'Meta Descrição (SEO)', placeholder: 'Fique por dentro das últimas notícias...', type: 'textarea', category: 'seo', hint: 'Descrição exibida nos resultados de busca (Google). Máx. 160 caracteres.' },
  { key: 'footer_about', label: 'Sobre o Site (Rodapé)', placeholder: 'Seu portal de notícias...', type: 'textarea', category: 'footer' },
  { key: 'footer_contact', label: 'Informações de Contato', placeholder: 'E-mail: contato@blog.com\nTel: (00) 0000-0000', type: 'textarea', category: 'footer' },
  { key: 'footer_copyright', label: 'Texto de Copyright', placeholder: '© 2026 Carlos Felipe o fera. Todos os direitos reservados.', type: 'text', category: 'footer' },
  { key: 'footer_privacy_link', label: 'Link — Política de Privacidade', placeholder: 'https://...', type: 'url', category: 'footer' },
  { key: 'footer_terms_link', label: 'Link — Termos de Uso', placeholder: 'https://...', type: 'url', category: 'footer' },
]

export default function FooterSettings() {
  const { user } = useAuth()
  const [values, setValues] = useState<Record<string, string>>({})
  const [ids, setIds] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)

  const fetchSettings = useCallback(async () => {
    setLoading(true)
    try {
      const keys = [...FIELDS.map(f => f.key), 'ga_measurement_id']
      const { list } = await lumi.entities.site_settings.list({
        filter: { key: { $in: keys } },
        limit: 30,
      })
      const vals: Record<string, string> = {}
      const idMap: Record<string, string> = {}
      ;(list as SiteSetting[]).forEach(s => {
        vals[s.key] = s.value
        idMap[s.key] = s._id
      })
      setValues(vals)
      setIds(idMap)
    } catch (e) {
      console.error('Erro ao carregar configurações:', e)
      toast.error('Erro ao carregar configurações.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchSettings() }, [fetchSettings])

  const handleSave = async (field: SettingField) => {
    const val = values[field.key] ?? ''
    setSaving(field.key)
    try {
      const now = new Date().toISOString()
      if (ids[field.key]) {
        await lumi.entities.site_settings.update(ids[field.key], {
          value: val,
          updatedAt: now,
        })
      } else {
        const created = await lumi.entities.site_settings.create({
          key: field.key,
          value: val,
          category: field.category,
          creator: user?.userId || '',
          createdAt: now,
          updatedAt: now,
        })
        setIds(prev => ({ ...prev, [field.key]: (created as SiteSetting)._id }))
      }
      toast.success(`"${field.label}" salvo com sucesso!`)
    } catch {
      toast.error('Erro ao salvar configuração.')
    } finally {
      setSaving(null)
    }
  }

  // --- Upload imagem do header ---
  const [headerImageUrl, setHeaderImageUrl] = useState<string>('')
  const [headerImageId, setHeaderImageId] = useState<string>('')
  const [uploadingHeader, setUploadingHeader] = useState(false)
  const headerInputRef = useRef<HTMLInputElement>(null)

  const fetchHeaderImage = useCallback(async () => {
    try {
      const { list } = await lumi.entities.site_settings.list({ filter: { key: 'header_image' }, limit: 1 })
      if (list[0]) {
        const s = list[0] as SiteSetting
        setHeaderImageUrl(s.value)
        setHeaderImageId(s._id)
      }
    } catch (e) {
      console.error('Erro ao carregar imagem do header:', e)
    }
  }, [])

  useEffect(() => { fetchHeaderImage() }, [fetchHeaderImage])

  const handleHeaderUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Apenas imagens são permitidas.')
      return
    }
    setUploadingHeader(true)
    try {
      const result = await lumi.tools.file.upload([file])
      const url = result?.[0]?.fileUrl || result?.[0]?.url || ''
      if (!url) throw new Error('URL não retornada')
      const now = new Date().toISOString()
      if (headerImageId) {
        await lumi.entities.site_settings.update(headerImageId, { value: url, updatedAt: now })
      } else {
        const created = await lumi.entities.site_settings.create({
          key: 'header_image',
          value: url,
          category: 'seo',
          creator: user?.userId || '',
          createdAt: now,
          updatedAt: now,
        })
        setHeaderImageId((created as SiteSetting)._id)
      }
      setHeaderImageUrl(url)
      toast.success('Imagem do header salva com sucesso!')
    } catch (e) {
      console.error(e)
      toast.error('Erro ao fazer upload da imagem do header.')
    } finally {
      setUploadingHeader(false)
    }
  }

  const handleHeaderRemove = async () => {
    if (!headerImageId) return
    try {
      await lumi.entities.site_settings.update(headerImageId, { value: '', updatedAt: new Date().toISOString() })
      setHeaderImageUrl('')
      toast.success('Imagem do header removida.')
    } catch {
      toast.error('Erro ao remover imagem.')
    }
  }

  // --- Upload logo do blog ---
  const [logoImageUrl, setLogoImageUrl] = useState<string>('')
  const [logoImageId, setLogoImageId] = useState<string>('')
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const logoInputRef = useRef<HTMLInputElement>(null)

  const fetchLogoImage = useCallback(async () => {
    try {
      const { list } = await lumi.entities.site_settings.list({ filter: { key: 'logo_image' }, limit: 1 })
      if (list[0]) {
        const s = list[0] as SiteSetting
        setLogoImageUrl(s.value)
        setLogoImageId(s._id)
      }
    } catch (e) {
      console.error('Erro ao carregar logo:', e)
    }
  }, [])

  useEffect(() => { fetchLogoImage() }, [fetchLogoImage])

  const handleLogoUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Apenas imagens são permitidas.')
      return
    }
    setUploadingLogo(true)
    try {
      const result = await lumi.tools.file.upload([file])
      const url = result?.[0]?.fileUrl || result?.[0]?.url || ''
      if (!url) throw new Error('URL não retornada')
      const now = new Date().toISOString()
      if (logoImageId) {
        await lumi.entities.site_settings.update(logoImageId, { value: url, updatedAt: now })
      } else {
        const created = await lumi.entities.site_settings.create({
          key: 'logo_image',
          value: url,
          category: 'seo',
          creator: user?.userId || '',
          createdAt: now,
          updatedAt: now,
        })
        setLogoImageId((created as SiteSetting)._id)
      }
      setLogoImageUrl(url)
      toast.success('Logo do blog salva com sucesso!')
    } catch (e) {
      console.error(e)
      toast.error('Erro ao fazer upload da logo.')
    } finally {
      setUploadingLogo(false)
    }
  }

  const handleLogoRemove = async () => {
    if (!logoImageId) return
    try {
      await lumi.entities.site_settings.update(logoImageId, { value: '', updatedAt: new Date().toISOString() })
      setLogoImageUrl('')
      toast.success('Logo removida. O ícone padrão será exibido.')
    } catch {
      toast.error('Erro ao remover logo.')
    }
  }

  const seoFields = FIELDS.filter(f => f.category === 'seo')
  const footerFields = FIELDS.filter(f => f.category === 'footer')

  const renderField = (field: SettingField) => (
    <div key={field.key} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-5">
      <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">{field.label}</label>
      {field.hint && <p className="text-xs text-gray-400 mb-2">{field.hint}</p>}
      <div className="flex gap-2 items-start">
        {field.type === 'textarea' ? (
          <textarea
            value={values[field.key] ?? ''}
            onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}
            placeholder={field.placeholder}
            rows={3}
            className="flex-1 px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors resize-none"
          />
        ) : (
          <input
            type={field.type || 'text'}
            value={values[field.key] ?? ''}
            onChange={e => setValues(prev => ({ ...prev, [field.key]: e.target.value }))}
            placeholder={field.placeholder}
            className="flex-1 px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
          />
        )}
        <button
          onClick={() => handleSave(field)}
          disabled={saving === field.key}
          className="px-3.5 py-2.5 bg-gray-900 dark:bg-gray-700 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center gap-1.5 flex-shrink-0"
        >
          {saving === field.key ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Salvar
        </button>
      </div>
    </div>
  )

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Configurações do Site</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">SEO, rodapé e informações institucionais</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-red-600" /></div>
      ) : (
        <>
          {/* Logo do Blog */}
          <div className="mb-8">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Logo do Blog</h2>
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-5">
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Logo do Cabeçalho</label>
              <p className="text-xs text-gray-400 mb-3">Substitui o ícone RC no menu de navegação. Recomendado: altura mínima de 72px, fundo transparente (PNG ou WebP).</p>

              {logoImageUrl ? (
                <div className="relative inline-block rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 mb-3 p-3 bg-gray-50 dark:bg-gray-800">
                  <img
                    src={logoImageUrl}
                    alt="Logo do blog"
                    className="h-14 w-auto object-contain"
                  />
                  <button
                    onClick={handleLogoRemove}
                    className="absolute top-1 right-1 p-1 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow"
                    title="Remover logo"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => logoInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all mb-3"
                  style={{ height: '90px' }}
                >
                  <ImageIcon size={24} className="text-gray-300" />
                  <span className="text-sm text-gray-400">Clique para fazer upload da logo</span>
                </div>
              )}

              <input
                ref={logoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleLogoUpload(f); e.target.value = '' }}
              />

              <button
                onClick={() => logoInputRef.current?.click()}
                disabled={uploadingLogo}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                {uploadingLogo ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {logoImageUrl ? 'Trocar logo' : 'Fazer upload'}
              </button>
            </div>
          </div>

          {/* Imagem do Header */}
          <div className="mb-8">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Imagem do Header</h2>
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-5">
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1">Imagem de Cabeçalho</label>
              <p className="text-xs text-gray-400 mb-3">Substitui o ícone e título do header. Recomendado: largura mínima de 1440px, altura livre. Formatos: JPG, PNG, WebP.</p>

              {headerImageUrl ? (
                <div className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 mb-3">
                  <img
                    src={headerImageUrl}
                    alt="Preview do header"
                    className="w-full object-cover"
                    style={{ maxHeight: '180px' }}
                  />
                  <button
                    onClick={handleHeaderRemove}
                    className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors shadow"
                    title="Remover imagem"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div
                  onClick={() => headerInputRef.current?.click()}
                  className="w-full border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-all mb-3"
                  style={{ height: '120px' }}
                >
                  <ImageIcon size={28} className="text-gray-300" />
                  <span className="text-sm text-gray-400">Clique para fazer upload da imagem do header</span>
                </div>
              )}

              <input
                ref={headerInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleHeaderUpload(f); e.target.value = '' }}
              />

              <button
                onClick={() => headerInputRef.current?.click()}
                disabled={uploadingHeader}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-gray-700 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors"
              >
                {uploadingHeader ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {headerImageUrl ? 'Trocar imagem' : 'Fazer upload'}
              </button>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Google Analytics</h2>
            <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-5">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-orange-50 dark:bg-orange-950/40 flex items-center justify-center flex-shrink-0">
                  <BarChart3 size={18} className="text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200">ID de medição GA4</label>
                  <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                    Liga o Google Analytics 4 ao site público. Cada visita à página inicial ou a uma matéria é enviada como page view real (sessões, origem, dispositivo e páginas). O painel admin não é medido.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 items-start">
                <input
                  type="text"
                  value={values.ga_measurement_id ?? ''}
                  onChange={e => setValues(prev => ({ ...prev, ga_measurement_id: e.target.value.trim() }))}
                  placeholder="G-XXXXXXXXXX"
                  className="flex-1 px-3.5 py-2.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
                />
                <button
                  onClick={() => handleSave({ key: 'ga_measurement_id', label: 'Google Analytics', placeholder: 'G-XXXXXXXXXX', type: 'text', category: 'seo' })}
                  disabled={saving === 'ga_measurement_id'}
                  className="px-3.5 py-2.5 bg-gray-900 dark:bg-gray-700 text-white text-sm font-semibold rounded-lg hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center gap-1.5 flex-shrink-0"
                >
                  {saving === 'ga_measurement_id' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  Salvar
                </button>
              </div>
              <a
                href="https://analytics.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:underline"
              >
                Abrir Google Analytics
                <ExternalLink size={11} />
              </a>
              <p className="text-xs text-gray-400 mt-2">
                Em analytics.google.com: Admin → Propriedade → Fluxos de dados → copie o ID de medição (começa por G-).
              </p>
            </div>
          </div>

          <div className="mb-6">
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">SEO e Identidade</h2>
            <div className="space-y-4">{seoFields.map(renderField)}</div>
          </div>
          <div>
            <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Rodapé</h2>
            <div className="space-y-4">{footerFields.map(renderField)}</div>
          </div>
        </>
      )}
    </div>
  )
}
